import { PlusIcon } from '@langchain/untitled-ui-icons';
import { FormControl, Switch } from '@mui/joy';

import { ReactNode, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useCreateOAuthProviderMutation } from '@/Pages/Settings/hooks/useOAuthProviders';
import { Dialog, DialogContent } from '@/components/Dialog/Dialog';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { Button, Input, Text } from '@/design-system';
import { usePermissions } from '@/hooks/usePermissions';

type CreateOAuthProviderFormData = {
  provider_id: string;
  name: string;
  client_id: string;
  client_secret: string;
  auth_url: string;
  token_url: string;
  uses_pkce: boolean;
  code_challenge_method: string;
};

function CreateOAuthProviderForm(props: {
  onSubmit: (data: CreateOAuthProviderFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
  error: string | null;
}) {
  'use no memo';
  const form = useForm<CreateOAuthProviderFormData>({
    defaultValues: {
      provider_id: '',
      name: '',
      client_id: '',
      client_secret: '',
      auth_url: '',
      token_url: '',
      uses_pkce: false,
      code_challenge_method: 'S256',
    },
  });

  return (
    <form
      onSubmit={form.handleSubmit(props.onSubmit)}
      className="flex flex-col gap-4"
    >
      <FormControl error={!!form.formState.errors.provider_id}>
        <Text variant="sm" weight="medium" as="label">
          Provider ID
        </Text>
        <Controller
          name="provider_id"
          control={form.control}
          rules={{
            required: 'Provider ID is required',
            pattern: {
              value: /^[a-zA-Z0-9_-]+$/,
              message:
                'Provider ID can only contain letters, numbers, hyphens, and underscores',
            },
          }}
          render={({ field }) => (
            <Input
              {...field}
              placeholder="e.g., github, google, slack"
              size="md"
            />
          )}
        />
        {form.formState.errors.provider_id && (
          <Text variant="xs" className="mt-1 text-red-500">
            {form.formState.errors.provider_id.message}
          </Text>
        )}
      </FormControl>

      <FormControl error={!!form.formState.errors.name}>
        <Text variant="sm" weight="medium" as="label">
          Display Name
        </Text>
        <Controller
          name="name"
          control={form.control}
          rules={{ required: 'Display name is required' }}
          render={({ field }) => (
            <Input
              {...field}
              placeholder="e.g., GitHub OAuth, Google Workspace"
              size="md"
            />
          )}
        />
        {form.formState.errors.name && (
          <Text variant="xs" className="mt-1 text-red-500">
            {form.formState.errors.name.message}
          </Text>
        )}
      </FormControl>

      <FormControl error={!!form.formState.errors.client_id}>
        <Text variant="sm" weight="medium" as="label">
          Client ID
        </Text>
        <Controller
          name="client_id"
          control={form.control}
          rules={{ required: 'Client ID is required' }}
          render={({ field }) => (
            <Input
              {...field}
              placeholder="OAuth application client ID"
              size="md"
            />
          )}
        />
        {form.formState.errors.client_id && (
          <Text variant="xs" className="mt-1 text-red-500">
            {form.formState.errors.client_id.message}
          </Text>
        )}
      </FormControl>

      <FormControl error={!!form.formState.errors.client_secret}>
        <Text variant="sm" weight="medium" as="label">
          Client Secret
        </Text>
        <Controller
          name="client_secret"
          control={form.control}
          rules={{ required: 'Client secret is required' }}
          render={({ field }) => (
            <Input
              {...field}
              type="password"
              placeholder="OAuth application client secret"
              size="md"
            />
          )}
        />
        {form.formState.errors.client_secret && (
          <Text variant="xs" className="mt-1 text-red-500">
            {form.formState.errors.client_secret.message}
          </Text>
        )}
      </FormControl>

      <FormControl error={!!form.formState.errors.auth_url}>
        <Text variant="sm" weight="medium" as="label">
          Authorization URL
        </Text>
        <Controller
          name="auth_url"
          control={form.control}
          rules={{
            required: 'Authorization URL is required',
            pattern: {
              value: /^https?:\/\/.+/,
              message: 'Must be a valid URL',
            },
          }}
          render={({ field }) => (
            <Input
              {...field}
              placeholder="https://example.com/oauth/authorize"
              size="md"
            />
          )}
        />
        {form.formState.errors.auth_url && (
          <Text variant="xs" className="mt-1 text-red-500">
            {form.formState.errors.auth_url.message}
          </Text>
        )}
      </FormControl>

      <FormControl error={!!form.formState.errors.token_url}>
        <Text variant="sm" weight="medium" as="label">
          Token URL
        </Text>
        <Controller
          name="token_url"
          control={form.control}
          rules={{
            required: 'Token URL is required',
            pattern: {
              value: /^https?:\/\/.+/,
              message: 'Must be a valid URL',
            },
          }}
          render={({ field }) => (
            <Input
              {...field}
              placeholder="https://example.com/oauth/token"
              size="md"
            />
          )}
        />
        {form.formState.errors.token_url && (
          <Text variant="xs" className="mt-1 text-red-500">
            {form.formState.errors.token_url.message}
          </Text>
        )}
      </FormControl>

      <div className="flex items-center justify-between">
        <Text variant="sm" weight="medium">
          Enable PKCE
        </Text>
        <Controller
          name="uses_pkce"
          control={form.control}
          render={({ field }) => (
            <Switch
              checked={field.value}
              onChange={(event) => field.onChange(event.target.checked)}
              sx={{
                '--Switch-thumbSize': '16px',
                '--Switch-trackWidth': '36px',
                '--Switch-trackHeight': '20px',
              }}
            />
          )}
        />
      </div>

      {props.error && <ExpandableErrorAlert error={props.error} />}

      <div className="flex justify-end gap-3">
        <Button
          variant="outlined"
          size="md"
          onClick={props.onCancel}
          disabled={form.formState.isSubmitting || props.isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="md"
          loading={form.formState.isSubmitting || props.isLoading}
          disabled={
            !form.watch('provider_id')?.trim() || !form.watch('name')?.trim()
          }
        >
          Create OAuth Provider
        </Button>
      </div>
    </form>
  );
}

export function CreateOAuthProviderModal(props: {
  className?: string;
  onSuccess?: () => void;
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { authorize } = usePermissions();
  const createProviderMutation = useCreateOAuthProviderMutation({
    onSuccess: () => {
      props.onSuccess?.();
      setOpen(false);
    },
  });

  const handleSubmit = (data: CreateOAuthProviderFormData) => {
    if (!authorize('workspaces:manage')) {
      return;
    }

    createProviderMutation.trigger({ json: data });
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          title="Create OAuth Provider"
          className="max-w-2xl border-b-0 [&>*]:border-b-0 [&_.dialog-header]:border-b-0 [&_[data-dialog-title]]:text-xl [&_[data-dialog-title]]:font-semibold"
        >
          <div className="flex flex-col gap-4">
            {createProviderMutation.error && (
              <ExpandableErrorAlert error={createProviderMutation.error} />
            )}
            <CreateOAuthProviderForm
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isLoading={createProviderMutation.isMutating}
              error={null}
            />
          </div>
        </DialogContent>
      </Dialog>
      <Button
        onClick={() => setOpen(true)}
        className={props.className}
        leftDecorator={PlusIcon}
      >
        OAuth Provider
      </Button>
    </>
  );
}
